<template>
  <div class="category-page" v-if="!loading">
    <img
      v-if="currentCategory.fields.pageBackground"
      :src="currentCategory.fields.pageBackground.fields.file.url"
      :width="currentCategory.fields.pageBackground.fields.file.details.image.width"
      :height="currentCategory.fields.pageBackground.fields.file.details.image.height"
      :title="currentCategory.fields.title"
      class="category-page__pageBackground"
    />
    <div 
      v-if="currentNode !== undefined"
      class="breadcrumbs__container">
      <BreadCrumbs :node="currentNode" :currentSlug="currentNode.fields.category.fields.slug" />
    </div>
    <div class="category-page__container">
      <div class="category-page__container__tags-categories-list">
        <div class="category-page__container__tags-categories-list__categories">
          <p>Categorie</p>
          <node :node="root" :activeNodes="getActiveNodes" />
        </div>
      </div>
      <div class="category-page__container__products-list">
        <h1
          :class="{ 'category--loading': loading }"
          class="category__title">{{ currentCategory.fields.title }}
        </h1>

        <div class="category-page__subCategories--mobile">
          <NodesList :nodes="nodesList" />
        </div>

        <div
          v-if="description !== ''"
          v-html="description.replace(/\n/gi, '<br />')"
          :style="`height: ${descriptionHeight}`"
          class="category__description" />

        <div
          v-if="description !== ''"
          class="view-more__category__description">
          <span v-if="descriptionHeight !== 'auto'" @click="descriptionHeight = 'auto'">Espandi</span>
          <span v-else @click="descriptionHeight = '90px'">Restringi</span>
        </div>

        <div
          class="home-category-listings home-category-listings--category-page"
          style="background-color: transparent;">
          <TagCategoryList
            v-if="typeof chunkArray[0] !== 'undefined'"
            :list="chunkArray[0]" />
          <TagCategoryList
            v-if="typeof chunkArray[1] !== 'undefined'"
            :list="chunkArray[1]"
            :rellaxSpeed="2" />
        </div>

        <!-- load more -->
        <div 
          v-if="!maxLoadReached"
          class="blog-posts-load-more">
          <span @click="$_loadMore">
            carica altri
          </span>
        </div>

      </div>
    </div>
    <FooterArea />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { splitEvery, transpose } from 'ramda'

import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

import BreadCrumbs from '@/components/BreadCrumbs/BreadCrumbs.vue'
import TagCategoryList from '@/components/TagCategoryList/TagCategoryList.vue'
import TagsByParentsList from '@/components/Menu/TagsByParentsList/TagsByParentsList.vue'
import Node from '@/components/Node/Node.vue'
import NodesList from '@/components/NodesList/NodesList.vue'
import FooterArea from '@/components/FooterArea/FooterArea.vue'

import matchMedia from '@/mixins/matchMedia.js'

const options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node, next) => {
      return `<div class="blog-post__content-container__image"><img src="${node.data.target.fields.file.url}" alt="" width="${node.data.target.fields.file.details.image.width}" height="${node.data.target.fields.file.details.image.height}" alt="${node.data.target.fields.title}"></div>`
    },
    [INLINES.ENTRY_HYPERLINK]: (node, next) => {
      const contentTypeId = node.data.target.sys.contentType.sys.id
      const map = {
        'products': 'prodotto',
        'category': 'categoria',
        'posts': 'cosa-usare-per',
        'tags': 'tag'
      }
      if (typeof map[contentTypeId] !== 'undefined'){
        return `<a class="underline_link" target="_blank" href="/${map[contentTypeId]}/${node.data.target.fields.slug}/">${node.content[0].value}</a>`
      }
      else return '<strong>Problem during link render</strong>'
    },
    [INLINES.HYPERLINK]: (node, next) => {
      return `<a class="underline_link" target="_blank" href="${node.data.uri}">${node.content[0].value}</a>`
    }
  },
  renderMark: {
    // Example
    [MARKS.BOLD]: text => `<strong>${text}</strong>`
    // [MARKS.CODE]
  }
}

export default {
  name: 'Category',
  mixins: [matchMedia],
  components: {
    BreadCrumbs,
    TagCategoryList,
    Node,
    NodesList,
    FooterArea
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data: () => ({
    observerRef: undefined,
    maxItems: 12,
    descriptionHeight: '90px'
  }),
  computed: {
    ...mapGetters('contentful', [
      'categories',
      'category',
      'products',
      'root',
      'nodes'
    ]),
    loading: {
      get () {
        return typeof this.currentCategory === 'undefined'
      }
    },
    currentCategory: {
      get () {
        return this.category(this.slug)
      }
    },
    categoryProducts: {
      get () {
        if (typeof this.currentNode === 'undefined') return []
        const categorizedPP = this.$_getCategoryProducts([this.currentNode])
        if (typeof this.currentNode.fields.products === 'undefined') {
          return categorizedPP.sort(() => {
            return .5 - Math.random();
          })
        }
        return categorizedPP
      }
    },
    chunkArray: {
      get () {
        if (this.loading) return [[], [], []]
        if (!this.categoryProducts.length) return [[], [], []]
        return transpose(splitEvery(2, this.categoryProducts.slice(0, this.maxItems)))
      }
    },
    description: {
      get () {
        if (this.loading) return ''
        if (typeof this.currentCategory.fields.description === 'undefined') return ''
        return documentToHtmlString(this.currentCategory.fields.description, options)
      }
    },
    currentNode: {
      get () {
        if (this.loading) return undefined
        return this.nodes.find(node => {
          return node.fields.category.fields.slug === this.slug
        })
      }
    },
    getActiveNodes: {
      get () {
        if (this.currentNode === undefined) return []
        return this.$_getActiveNodes(this.currentNode)
      }
    },
    nodesList: {
      get () {
        if (this.currentNode === undefined) return undefined
        if (typeof this.currentNode.fields.nodesList === 'undefined') return this.getParent.fields.nodesList
        return this.currentNode.fields.nodesList
      }
    },
    getParent: {
      get () {
        return this.nodes.find(node => {
          if (typeof node.fields.nodesList === 'undefined') return false
          return node.fields.nodesList.find(node => {
            return node.sys.id === this.currentNode.sys.id
          }) !== undefined
        })
      }
    },
    maxLoadReached: {
      get () {
        if (this.loading) return true
        return this.maxItems >= this.categoryProducts.length
      }
    }
  },
  beforeRouteEnter (from, to, next) {
    next(async (vm) => {
	    await vm.loadData()
      if (typeof vm.currentCategory === 'undefined') vm.$router.replace({ name: 'not-found-page' })
    })
  },
  beforeRouteUpdate (from, to, next) {
    this.loadData()
    next()
  },
  methods: {
    ...mapActions('contentful', [
      'loadProducts',
      'loadCategories',
      'loadRoot',
      'loadNodes'
    ]),
    $_loadMore () {
      this.maxItems = this.maxItems + 12
    },
    $_getActiveNodes (current) {
      if (current.sys.id === this.root.sys.id) return []
      // se nodo, ritorno il suo ID e chiamo al padre
      const parent = this.nodes.find(node => {
        if (typeof node.fields.nodesList === 'undefined') return false
        return node.fields.nodesList.find(node => {
          return node.sys.id === current.sys.id
        }) !== undefined
      })
      if (parent === undefined) return [current.sys.id]
      return [current.sys.id].concat(this.$_getActiveNodes(parent))
    },
    $_getCategoryProducts (nodes) {
      var products = []
      nodes.forEach(node => {
        if (typeof node.fields.products !== 'undefined') products = products.concat(node.fields.products)
        if (typeof node.fields.nodesList !== 'undefined') products = products.concat(this.$_getCategoryProducts(node.fields.nodesList))
      })
      return products
    },
    async loadData () {
      try {
        await Promise.all([this.loadCategories(), this.loadProducts(), this.loadRoot(), this.loadNodes()])
        window.prerenderReady = true
      } catch (e) {
        // TODO sentry
      }
    }
  },
  metaInfo () {
    if (!this.loading) return {
      title: this.currentCategory.fields.seoTitle.length >= 40 ? `${this.currentCategory.fields.seoTitle} | Colorificio Postumia` : `${this.currentCategory.fields.title} | Colorificio Postumia`,
      meta: [
        { vmid: 'description', name: 'description', content: this.currentCategory.fields.seoDescription.length >= 100 ? this.currentCategory.fields.seoDescription : `${this.currentCategory.fields.title} delle migliori marche a prezzi vantaggiosi. ${this.currentCategory.fields.title} in vendita nei negozi del Colorificio Postumia.` },
        { property: 'og:locale', content: 'it' },
        { property: 'og:title', content: this.currentCategory.fields.seoTitle.length >= 40 ? `${this.currentCategory.fields.seoTitle} | Colorificio Postumia` : `${this.currentCategory.fields.title} | Colorificio Postumia` },
        { property: 'og:description', content: this.currentCategory.fields.seoDescription.length >= 100 ? this.currentCategory.fields.seoDescription : `${this.currentCategory.fields.title} delle migliori marche a prezzi vantaggiosi. ${this.currentCategory.fields.title} in vendita nei negozi del Colorificio Postumia.` },
        { property: 'og:url', content: `/assortimento/${this.slug}` }
      ],
      link: [
        { rel: 'canonical', href: location.protocol + '//' + location.host + location.pathname }
      ]
    }
    return {
      title: 'Loading...',
      meta: [
        { vmid: 'description', name: 'description', content: 'Loading...' },
        { property: 'og:locale', content: 'Loading...' },
        { property: 'og:title', content: 'Loading...' },
        { property: 'og:description', content: 'Loading...' },
        { property: 'og:url', content: 'Loading...' }
      ],
      link: [
        { rel: 'alternate', href: 'Loading...', hreflang: 'it' }
      ]
    }
  }
}
</script>

<style lang="scss">
  @import "./component.scss";

  .blog-posts-load-more{
    padding: 2rem 0 6rem;
    @include media(">=large"){
      padding: 3rem 0 6rem;
    }
    text-align: center;
    span{
      @include button-link($color: #333, $borderColor: #333, $hover: #FFFFEA);
    }
  }
  .view-more__category__description{
    padding: 2rem 0 6rem;
    @include media(">=large"){
      padding: 3rem 0 6rem;
    }
    text-align: left;
    span{
      @include button-link($color: #333, $borderColor: #333, $hover: #FFFFEA);
    }
  }
</style>
